import { Injectable } from '@angular/core';
import { HttpStatusCode } from '@angular/common/http';
import algolia, { SearchClient } from 'algoliasearch';
import {
  ALGOLIA_APP_ID,
  ALGOLIA_CACHE_TIMEOUT,
  ALGOLIA_USER_TOKEN,
  ATTR_MAP,
  Connection,
  DataSource,
  Query,
  QueryOptions,
  QueryResults,
  RecordNames,
  RecordTypes,
  SearchFetchService,
  SearchSettingsResource,
  SETTINGS_MAP,
  SHOWCASE_TABLE_NAMES,
  Space,
  UserGroup,
} from '@mode/shared/data-access-algolia';
import { createDeferredPromise, isPresent, MetaTags, MetaTagsService } from '@mode/shared/util-js';
import { ObservabilityEvents, ObservabilityService, WEBAPP_UI_NAMESPACE } from '@mode/shared/util-observability';
import { FeatureFlag, FeatureFlagsFacade } from '@mode/shared/contract-common';
import { AlgoliaSearchFacade } from './algolia-search/algolia-search.facade';
import { filter, first, map as rxjsMap } from 'rxjs/operators';
import { Hit, MultipleQueriesResponse, SearchResponse } from '@algolia/client-search';
import { Table } from '@mode/shared/contract-common';
import { forOwn, keys, map, merge, reduceRight, size } from 'lodash';
import { format } from 'date-fns';
import { firstValueFrom } from 'rxjs';
import { SearchSettingsModel } from './+state/algolia-search.models';
import * as AlgoliaSearchHelpers from './algolia-search-helpers/algolia-search-helpers';
import { User } from '@mode/algolia/contract';
import { Attributes, AttributeValue } from '@opentelemetry/api';

@Injectable()
export class SearchClientService {
  public settingsLastSetAt = 0; // unix timestamp of when algolia settings+key were last set
  public keyDurationValue = 0; // how frequently algolia settings+key should last, when value is 0 then the settings should not expire
  private _indexMap: any = {};
  private _algoliaMap: any = {};
  private _indexClientMap: any = {};
  private _clients: { [apiKey: string]: SearchClient } = {}; // Mapping of all algolia clients created by search service, one per index
  private _attrMap = ATTR_MAP; // Mapping of search record to fetchable attributes for the given record
  private _settingsResponseMap: any = SETTINGS_MAP; // Mapping from algolia index name to search api model name
  private _deferredClients = createDeferredPromise();
  private _showcaseTableArray = SHOWCASE_TABLE_NAMES?.split(',') ?? [];
  private _searchCacheTimeoutId: NodeJS.Timeout | undefined;
  private _searchCacheTimeoutDuration = ALGOLIA_CACHE_TIMEOUT;
  private scimText = 'SCIM';

  constructor(
    private _featureFlagsFacade: FeatureFlagsFacade,
    private searchFetchService: SearchFetchService,
    private metaTag: MetaTagsService,
    private algoliaSearchFacade: AlgoliaSearchFacade,
    private observabilityService: ObservabilityService
  ) {
    this._featureFlagsFacade
      .featureValueAsObservable<number>(FeatureFlag.AlgoliaKeyDuration)
      .pipe(first())
      .subscribe((duration) => {
        if (duration) {
          this.keyDurationValue = duration * 1000; // convert seconds to milliseconds
        }
        // Initialize algolia clients per algolia search setting
        this._getAndSetSearchSettings();
      });

    this._featureFlagsFacade.asObservable(FeatureFlag.AzureActiveDirectoryScim).subscribe((enabled) => {
      this.scimText = enabled ? 'SCIM' : 'Okta SCIM';
    });
  }

  public get INDEX_MAP() {
    return this._algoliaMap;
  }

  public async query<K extends RecordNames>(opts: QueryOptions<K>): Promise<QueryResults<K>>;
  public async query(opts: QueryOptions<RecordNames> & { rawResults: true }): Promise<MultipleQueriesResponse<unknown>>;
  public async query<K extends RecordNames>(
    opts: QueryOptions<K> & { rawResults?: true }
  ): Promise<QueryResults<K> | MultipleQueriesResponse<unknown>> {
    let queryNeedsNewSettings = false;

    if (this.keyDurationValue > 0) {
      queryNeedsNewSettings = this._hasTimeDifferenceOver(this.settingsLastSetAt, Date.now(), this.keyDurationValue);
    }

    const refetchSettingsPromise = queryNeedsNewSettings
      ? this._fetchAlgoliaSearchSettings(true).then((settings: any) => {
          this._setAlgoliaSearchSettings(settings);
        })
      : Promise.resolve();

    return refetchSettingsPromise.then(() => {
      return this._deferredClients.promise.then(() => this._handleQuery(opts));
    });
  }

  public showcaseTables(opts: { user: string; org: string }): Promise<any> {
    return this._deferredClients.promise.then(() => {
      const deferred = createDeferredPromise();

      // make a copy of showcase tables to insert user specific schemas
      let filters = this._showcaseTableArray.slice();
      filters.push(opts.user);
      filters.push(opts.org);
      filters = filters.map((name) => 'schema_' + name);
      const searchOpts = filters.map((filter) => {
        const filterOps = {
          hitsPerPage: 50,
          attributesToRetrieve: this._attrMap['tableAlphabetical'],
          restrictSearchableAttributes: ['schema'],
          tagFilters: [filter],
        };
        return {
          indexName: this._indexMap['tableAlphabetical'],
          query: '',
          params: filterOps,
        };
      });

      const client = this._clients[this._indexClientMap['tableAlphabetical']];

      client
        .search(searchOpts)
        .then(function (response) {
          let result: Hit<Table>[] = [];
          response.results.forEach((res) => {
            result = result.concat((res as SearchResponse<Table>).hits);
          });
          deferred.resolve(result);
        })
        .catch(deferred.reject);

      if (this._searchCacheTimeoutId) {
        clearTimeout(this._searchCacheTimeoutId);
      }
      this._searchCacheTimeoutId = setTimeout(() => {
        client.clearCache();
      }, this._searchCacheTimeoutDuration);

      return deferred.promise;
    });
  }

  public createConnectionFromDatasourceHit(datasource: DataSource): Connection {
    let nameHighlight = datasource.name;
    let dataSourceTypeHighlight = datasource.dataSourceType;
    const highlight = datasource._highlightResult;

    if (datasource._highlightResult) {
      if (highlight.name) {
        nameHighlight = highlight.name.value;
      }
      if (highlight.dataSourceType) {
        dataSourceTypeHighlight = highlight.dataSourceType.value;
      }
    }

    return {
      name: nameHighlight,
      datasource_type: dataSourceTypeHighlight,
      connection_type: datasource.bridged ? 'Bridge' : 'Direct Connect',
      created_at: this._formatTime(datasource.createdAt * 1000),
    };
  }

  public createGroupsFromHit(groupHit: UserGroup) {
    let nameHighlight = groupHit.name;
    const highlight = groupHit._highlightResult;

    if (groupHit._highlightResult) {
      if (highlight.name) {
        nameHighlight = highlight.name.value;
      }
    }

    return {
      name: nameHighlight,
      member_count: groupHit.memberCount,
      created_at: this._formatTime(groupHit.createdAt * 1000),
      webPath: groupHit.webPath,
      managed_by: groupHit.scimProvisioned ? this.scimText : 'Mode',
    };
  }

  public createGroupMemberFromHit(groupMemberHit: User) {
    return {
      name: groupMemberHit.name,
      email: null,
      username: groupMemberHit.username,
      status: null,
      token: groupMemberHit.token,
      avatar: {
        avatar: groupMemberHit.avatar,
        size: 30,
      },
    };
  }

  public createGroupSpaceFromHit(groupSpaceHit: Space) {
    return {
      name: groupSpaceHit.name,
      reports: groupSpaceHit.reportCount,
      createdBy: groupSpaceHit.createdBy,
      createdOn: this._formatTime(groupSpaceHit.createdAt * 1000),
    };
  }

  public createGroupConnectionFromHit(groupConnectionHit: DataSource) {
    return {
      name: groupConnectionHit.name,
      dataSource: groupConnectionHit.dataSourceType,
      createdBy: groupConnectionHit.creatorName,
      createdOn: this._formatTime(groupConnectionHit.createdAt * 1000),
    };
  }

  private async _handleQuery<K extends RecordNames>(opts: QueryOptions<K>): Promise<QueryResults<K>>;
  private async _handleQuery(
    opts: QueryOptions<RecordNames> & { rawResults: true }
  ): Promise<MultipleQueriesResponse<unknown>>;
  private async _handleQuery<K extends RecordNames>(
    opts: QueryOptions<K> & { rawResults?: true }
  ): Promise<QueryResults<K> | MultipleQueriesResponse<unknown>> {
    const clientRequests: any = this.getClientRequests(opts);

    if (size(clientRequests) == 1) {
      const key = keys(clientRequests)[0];
      return this._executeClientSearch(this._clients[key], clientRequests[key], opts);
    } else {
      return Promise.all(
        map(clientRequests, (clientRequest: any, key) =>
          this._executeClientSearch(this._clients[key], clientRequest, opts)
        )
      ).then((results) => this._mergeSearchResults(opts.rawResults, results as any));
    }
  }

  getClientRequests<K extends keyof RecordTypes>(opts: QueryOptions<K> & { rawResults?: true }) {
    return (
      opts.indexes
        // exclude invalid indexes
        .filter((i: any) => this._indexMap[i])
        // get client key and request options
        .map((index): [string, any] => [
          this._indexClientMap[index],
          this._buildIndexOptions(opts as any, index as any),
        ])
        // map request options to client key
        .reduce((accu: { [key: string]: any }, [clientKey, opts]: [string, any]) => {
          accu[clientKey] = [...(accu[clientKey] ?? []), opts];
          return accu;
        }, {})
    );
  }

  private _buildIndexOptions(opts: QueryOptions<RecordNames>, index: RecordNames) {
    const batchOpts: any = {
      hitsPerPage: opts.perPage || 20,
      attributesToRetrieve: opts.attrsToRetrieve || this._attrMap[index],
    };
    if (opts.filters && (opts.filters as any)[index]) {
      batchOpts.tagFilters = (opts.filters as any)[index];
    } else if (typeof opts.filters === 'string') {
      batchOpts.tagFilters = opts.filters;
    }
    if (opts.facets && opts.facets[index]) {
      batchOpts.facetFilters = opts.facets[index];
      batchOpts.facets = '*';
    }
    if (opts.page && (opts.page as any)[index]) {
      batchOpts.page = (opts.page as any)[index];
    } else if (typeof opts.page === 'number') {
      batchOpts.page = opts.page;
    }
    if (opts.range) {
      batchOpts.numericFilters = opts.range;
    }
    if (opts.queryType) {
      batchOpts.queryType = opts.queryType;
    }
    if (opts.typoTolerance) {
      batchOpts.typoTolerance = opts.typoTolerance;
    }
    if (opts.advancedSyntax) {
      batchOpts.advancedSyntax = opts.advancedSyntax;
    }
    if (opts.attrsToSearchIn && opts.attrsToSearchIn[index]) {
      batchOpts.restrictSearchableAttributes = opts.attrsToSearchIn[index];
    }
    if (opts.useUniqueKey) {
      batchOpts.distinct = 1;
    }
    const indexOptions = {
      indexName: this._indexMap[index],
      query: opts.query,
      params: batchOpts,
    };
    return indexOptions;
  }

  private _executeClientSearch<K extends RecordNames>(
    client: SearchClient,
    searchOpts: Query,
    opts: QueryOptions<K>
  ): Promise<QueryResults<K>>;
  private _executeClientSearch(
    client: SearchClient,
    searchOpts: Query,
    opts: QueryOptions<RecordNames> & { rawResults: true }
  ): Promise<MultipleQueriesResponse<unknown>>;
  private _executeClientSearch<K extends RecordNames>(
    client: SearchClient,
    searchOpts: Query,
    opts: QueryOptions<K> & { rawResults?: true }
  ): Promise<any> {
    const deferred = createDeferredPromise();

    if (opts.clearCache) {
      client.clearCache();
    }

    this.startSearchObservability(searchOpts, opts.attrsToSearchIn);

    client
      .search(searchOpts)
      .then((res) => {
        this.observabilityService.endTrace(ObservabilityEvents.DATASOURCE_SUGGESTION_SEARCH, Date.now(), {
          [`${WEBAPP_UI_NAMESPACE}.search.response.code`]: HttpStatusCode.Ok,
        });
        return this._getClientResponse(opts, res);
      })
      .then(deferred.resolve)
      .catch((err) => {
        console.warn(err.message);
        this.observabilityService.endTrace(ObservabilityEvents.DATASOURCE_SUGGESTION_SEARCH, Date.now(), {
          [`${WEBAPP_UI_NAMESPACE}.search.response.code`]: err.statusCode || err.status,
        });
        // 400 status codes are often caused by the API key expiring
        // so if its a 400, attempt to get and reset search settings keys
        if (err.statusCode === 400) {
          console.warn('attempting to reset search settings...');
          this._getAndSetSearchSettings(true);
        }
        return deferred.reject();
      });

    return deferred.promise;
  }

  /**
   * @return Correct format of search client
   * response object given the options passed
   */
  private _getClientResponse<K extends RecordNames>(
    opts: QueryOptions<K>,
    response: MultipleQueriesResponse<unknown>
  ): QueryResults<K>;
  private _getClientResponse(
    opts: QueryOptions<RecordNames> & { rawResults: true },
    response: MultipleQueriesResponse<unknown>
  ): MultipleQueriesResponse<unknown>;
  private _getClientResponse<K extends RecordNames>(
    opts: QueryOptions<K> & { rawResults?: true },
    response: MultipleQueriesResponse<unknown>
  ): QueryResults<K> | MultipleQueriesResponse<unknown> {
    if (opts.rawResults) {
      return response;
    } else {
      const result: any = {};
      response.results.forEach((res) => {
        // TODO figure out a way to not typecast
        const resAny = res as SearchResponse<unknown>;

        result[this._algoliaMap[resAny.index as string]] = resAny.hits;
        result['count'] = resAny.nbHits;
        result['page'] = resAny.page;
        result['totalPages'] = resAny.nbPages;
      });
      return result;
    }
  }

  // The only way to type this correctly is with conditional non-nullable types, but all our types are nullable
  private _mergeSearchResults<R extends QueryResults<RecordNames> | MultipleQueriesResponse<unknown>>(
    rawResults: true | undefined,
    results: R[]
  ): R;
  private _mergeSearchResults(
    rawResults: true | undefined,
    results: Array<QueryResults<RecordNames> | MultipleQueriesResponse<unknown>>
  ): QueryResults<RecordNames> | MultipleQueriesResponse<unknown> {
    if (rawResults) {
      // raw results are in the form:
      // [{ results: [...] }, { results: [...] }, ...]
      // and should be flattened to:
      // [{ results: [...] }]
      return {
        results: reduceRight(results, (flattened: any[], next: any) => flattened.concat(next.results), []),
      };
    } else {
      // parsed results are in the form:
      // [ [{ index: 'a' }, { index: 'b' }], [{ index: 'c' }] ]
      // and should be merged to:
      // [ [{ index: 'a' }, { index: 'b' }, { index: 'c' }] ]

      // eslint-disable-next-line prefer-spread
      return merge.apply(null, results as any);
    }
  }

  private _formatTime(time: number): string {
    return format(time, 'PP');
  }

  private _hasTimeDifferenceOver(date1 = 0, date2 = 0, timeDifference = ALGOLIA_CACHE_TIMEOUT) {
    return Math.abs(date1 - date2) > timeDifference;
  }

  private async _getAndSetSearchSettings(refresh = false): Promise<void> {
    const settings = await this._fetchAlgoliaSearchSettings(refresh);
    this._setAlgoliaSearchSettings(settings);
    this._deferredClients.resolve();
  }

  private _setAlgoliaSearchSettings(settings: any): void {
    forOwn(settings, ({ key: apiKey, indexName }, key) => {
      const appId = ALGOLIA_APP_ID ?? '';
      const appUserToken = ALGOLIA_USER_TOKEN ?? '';
      if (!this._clients[apiKey]) {
        const client = algolia(appId, apiKey, { headers: { 'x-algolia-usertoken': appUserToken } });
        this.settingsLastSetAt = Date.now();
        this._clients[apiKey] = AlgoliaSearchHelpers.hackMoveSearchClientApiKeyFromHeaderToRequestBody(client, apiKey);
      }
      this._algoliaMap[indexName] = this._settingsResponseMap[key];
      this._indexMap[this._settingsResponseMap[key]] = indexName;
      this._indexClientMap[this._algoliaMap[indexName]] = apiKey;
    });
  }

  private async _fetchAlgoliaSearchSettings(refresh = false): Promise<any> {
    if (refresh) {
      return this.searchFetchService.getSearchSettingsPromise();
    }

    return firstValueFrom(
      this.algoliaSearchFacade.settings$.pipe(
        filter(isPresent),
        rxjsMap((settings: SearchSettingsModel): SearchSettingsResource['settings'] => ({
          ['Collection-relevance']: settings.collection_relevance,
          ['DataSource-relevance']: settings.datasource_relevance,
          ['DataSourceTable-relevance']: settings.datasource_tableRelevance,
          ['Definition-alphabetical']: settings.definition_alphabetical,
          ['Definition-relevance']: settings.definition_relevance,
          ['Organization-relevance']: settings.organization_relevance,
          ['Report-alphabetical']: settings.report_alphabetical,
          ['Report-alphabetical-desc']: settings.report_alphabeticalDesc,
          ['Report-last-edited']: settings.report_lastEdited,
          ['Report-relevance']: settings.report_relevance,
          ['Table-alphabetical']: settings.table_alphabetical,
          ['Table-relevance']: settings.table_relevance,
          ['User-email']: settings.user_email,
          ['User-relevance']: settings.user_relevance,
          ['UserGroup-relevance']: settings.userGroup_relevance,
          ...(settings.global_search && {
            ['global-search']: settings.global_search,
          }),
        }))
      )
    );
  }

  private startSearchObservability<K extends RecordNames>(
    searchOpts: Query,
    attrsToSearchIn: { [key in K]: (keyof RecordTypes[key])[] } | undefined
  ) {
    // Regular expression to match either a 12-character ID or a UUID from URL
    const idRegex = /\b(?:[0-9a-fA-F]{12}\b|\b[0-9a-fA-F]{8}(?:-[0-9a-fA-F]{4}){3}-[0-9a-fA-F]{12}\b)/g;
    const attrs: Attributes = {
      [`${WEBAPP_UI_NAMESPACE}.search.query`]: searchOpts.map((opts) => opts.query),
      [`${WEBAPP_UI_NAMESPACE}.search.indexes`]: searchOpts.map((opt) => opt.indexName).join(' '),
      [`${WEBAPP_UI_NAMESPACE}.search.dataSourceTable`]: `${searchOpts.map((opt) => opt.params?.tagFilters)}`,
      [`${WEBAPP_UI_NAMESPACE}.search.org`]:
        this.metaTag.getTag(MetaTags.OwnerUsername) || this.metaTag.getTag(MetaTags.ReportOwnerUsername) || '',
      [`${WEBAPP_UI_NAMESPACE}.url`]: window.location.pathname.replace(idRegex, '*'),
    };
    // unpack the dynamic 'attrsToSearchIn' obj and add each one as an attribute.
    if (attrsToSearchIn) {
      Object.entries(attrsToSearchIn).forEach(([key, value]) => {
        attrs[`${WEBAPP_UI_NAMESPACE}.search.${key}`] = value as AttributeValue;
      });
    }

    this.observabilityService.startTrace(ObservabilityEvents.DATASOURCE_SUGGESTION_SEARCH, {
      startTime: Date.now(),
      attributes: attrs,
    });
  }
}
